import {
  FileCheck,
  History,
  IdCard,
  Mail,
  ReceiptText,
  ShieldCheck,
} from 'lucide-react';
import type { DetailsTabMenuLink } from '@/components/details';
import type { DetailsSearch } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';

/**
 * Zakładki ekranów modułu zmian rezerwacji. Wartości to **segmenty ścieżki**,
 * a nie parametry wyszukiwania — każda zakładka jest osobną podstroną rekordu
 * (`/app/participants/{id}/info`). Polskie są wyłącznie etykiety.
 *
 * Każdy ekran ma własny enum i własną listę, nawet gdy zestawy dziś się
 * pokrywają: zakładki uczestnika i zapisującego wolno rozejść, bo to osobne
 * ekrany osobnych rekordów.
 *
 * Etykieta, znak i podpis na kaflu menu mobilnego stoją przy literale ścieżki
 * i nigdzie indziej. Literał powtarza się przy każdej zakładce zamiast sklejać
 * segment z wyliczenia — tylko wtedy sprawdza się przy kompilacji o drzewo tras.
 *
 * `search` wędruje do każdej zakładki, bo tryb edycji dotyczy całego ekranu
 * i przełączenie zakładki nie ma prawa go zgubić.
 */

export enum ParticipantDetailsTab {
  INFO = 'info',
  BOOKING = 'booking',
}

export const participantTabLinks = (
  participantId: string,
  search: DetailsSearch,
): DetailsTabMenuLink[] => [
  {
    label: 'Informacje',
    icon: IdCard,
    hint: 'Dane osobowe i zgody',
    to: '/app/participants/$participantId/info',
    params: { participantId },
    search,
  },
  {
    label: 'Rezerwacja',
    icon: ReceiptText,
    hint: 'Rachunek i raty',
    to: '/app/participants/$participantId/booking',
    params: { participantId },
    search,
  },
];

export enum ClientDetailsTab {
  INFO = 'info',
  BOOKING = 'booking',
}

export const clientTabLinks = (
  clientId: string,
  search: DetailsSearch,
): DetailsTabMenuLink[] => [
  {
    label: 'Informacje',
    icon: IdCard,
    hint: 'Dane zapisującego',
    to: '/app/clients/$clientId/info',
    params: { clientId },
    search,
  },
  {
    label: 'Rezerwacja',
    icon: ReceiptText,
    hint: 'Rachunek i raty',
    to: '/app/clients/$clientId/booking',
    params: { clientId },
    search,
  },
];

export enum BookingDetailsTab {
  BOOKING = 'booking',
  CONSENTS = 'consents',
  INSURANCES = 'insurances',
  MESSAGES = 'messages',
  HISTORY = 'history',
}

export const bookingTabLinks = (
  bookingId: string,
  search: DetailsSearch,
): DetailsTabMenuLink[] => [
  {
    label: 'Rezerwacja',
    icon: ReceiptText,
    hint: 'Rachunek całej rezerwacji',
    to: '/app/bookings/$bookingId/booking',
    params: { bookingId },
    search,
  },
  {
    label: 'Zgody',
    icon: FileCheck,
    hint: 'Co jest podpisane',
    to: '/app/bookings/$bookingId/consents',
    params: { bookingId },
    search,
  },
  {
    label: 'Ubezpieczenia',
    icon: ShieldCheck,
    hint: 'Kto jest ubezpieczony',
    to: '/app/bookings/$bookingId/insurances',
    params: { bookingId },
    search,
  },
  {
    label: 'Wiadomości',
    icon: Mail,
    hint: 'Co poszło do klienta',
    to: '/app/bookings/$bookingId/messages',
    params: { bookingId },
    search,
  },
  {
    label: 'Historia',
    icon: History,
    hint: 'Wprowadzone paczki zmian',
    to: '/app/bookings/$bookingId/history',
    params: { bookingId },
    search,
  },
];
